import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api, type RateLimitConfigResponse, type RateLimitConfig } from '../api/client';

export function RateLimits() {
  const { currentWorkspaceId: workspaceId } = useAuth();

  const [data, setData] = useState<RateLimitConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<RateLimitConfig>({});

  const fetchData = () => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    api.getRateLimits(workspaceId)
      .then((d) => {
        setData(d);
        setDraft(d.config);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load rate limits.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [workspaceId]);

  const handleSave = async () => {
    if (!workspaceId) return;
    setSaving(true);
    setError(null);
    try {
      const { enabled: _, ...overrides } = draft;
      await api.updateRateLimits(workspaceId, overrides);
      fetchData();
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save rate limits');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!workspaceId) return;
    setSaving(true);
    setError(null);
    try {
      await api.deleteRateLimits(workspaceId);
      fetchData();
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset rate limits');
    } finally {
      setSaving(false);
    }
  };

  if (!workspaceId) {
    return (
      <div className="rounded-xl border border-border-dark bg-surface-1 p-8 text-center text-sm text-text-secondary">
        No workspace found. Please complete onboarding first.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 spinner-gradient" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-[rgba(248,113,113,0.2)] bg-[rgba(248,113,113,0.12)] px-4 py-3 text-sm text-[var(--color-error)]">
        {error}
      </div>
    );
  }

  if (!data) return null;

  type NumericField = 'actor_max_per_window' | 'workspace_max_per_window' | 'window_ms';
  const fields: { label: string; field: NumericField; unit: string; description: string }[] = [
    { label: 'Actor Max per Window', field: 'actor_max_per_window', unit: 'requests', description: 'Maximum requests per actor within the time window' },
    { label: 'Workspace Max per Window', field: 'workspace_max_per_window', unit: 'requests', description: 'Maximum requests per workspace within the time window' },
    { label: 'Window Duration', field: 'window_ms', unit: 'ms', description: 'Sliding window size in milliseconds' },
  ];

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-text-primary">Rate Limits</h1>
            {data.is_custom ? (
              <span className="rounded-full bg-accent-teal/15 px-2.5 py-0.5 text-xs font-medium text-accent-teal">Custom</span>
            ) : (
              <span className="rounded-full bg-surface-2 px-2.5 py-0.5 text-xs font-medium text-text-tertiary">Default</span>
            )}
          </div>
          <p className="mt-1 text-sm text-text-secondary">
            Configure rate limiting thresholds for this workspace
          </p>
        </div>
        <div className="flex gap-2">
          {editing ? (
            <>
              <button
                onClick={() => { setEditing(false); setDraft(data.config); }}
                className="rounded-lg border border-border-dark px-4 py-2 text-sm text-text-secondary hover:bg-surface-2 transition-colors"
                disabled={saving}
              >
                Cancel
              </button>
              {data.is_custom && (
                <button
                  onClick={handleReset}
                  className="rounded-lg border border-[rgba(248,113,113,0.3)] px-4 py-2 text-sm text-[var(--color-error)] hover:bg-[rgba(248,113,113,0.08)] transition-colors"
                  disabled={saving}
                >
                  Reset to Default
                </button>
              )}
              <button
                onClick={handleSave}
                className="rounded-lg bg-accent-teal px-4 py-2 text-sm font-medium text-white hover:bg-accent-teal/90 transition-colors"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </>
          ) : (
            <button
              onClick={() => setEditing(true)}
              className="rounded-lg bg-accent-teal px-4 py-2 text-sm font-medium text-white hover:bg-accent-teal/90 transition-colors"
            >
              Edit
            </button>
          )}
        </div>
      </div>

      {/* Status */}
      <div className="mb-6 rounded-xl border border-border-dark bg-surface-1 p-5">
        <div className="flex items-center gap-3">
          <span className={`h-2.5 w-2.5 rounded-full ${data.config.enabled ? 'bg-accent-teal' : 'bg-text-tertiary'}`} />
          <span className="text-sm font-medium text-text-primary">
            Rate limiting is {data.config.enabled ? 'enabled' : 'disabled'}
          </span>
        </div>
      </div>

      <div className="grid gap-6 sm:grid-cols-3">
        {fields.map(({ label, field, unit, description }) => {
          const value = editing ? draft[field] : data.config[field];

          return (
            <div key={field} className="rounded-xl border border-border-dark bg-surface-1 p-6">
              <h3 className="text-sm font-medium text-text-secondary">{label}</h3>
              <p className="mt-0.5 text-xs text-text-tertiary">{description}</p>
              {editing ? (
                <div className="mt-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      className="w-full rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary focus:border-accent-teal focus:outline-none"
                      value={draft[field] ?? ''}
                      onChange={(e) => {
                        const val = e.target.value === '' ? undefined : parseInt(e.target.value);
                        setDraft({ ...draft, [field]: val });
                      }}
                    />
                    <span className="shrink-0 text-xs text-text-tertiary">{unit}</span>
                  </div>
                </div>
              ) : (
                <p className="mt-3 text-2xl font-bold text-text-primary">
                  {value !== undefined ? (
                    <>
                      {field === 'window_ms' ? `${(Number(value) / 1000).toFixed(0)}s` : value?.toLocaleString()}
                    </>
                  ) : (
                    <span className="text-text-tertiary">Not set</span>
                  )}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
